import React, { useRef, useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Box, BoxProps, useTheme } from '@mui/material'

const DEFAULT_ZOOM = 2.5
const DEFAULT_LENS_SIZE = 300
const LENS_CURSOR_GAP = 16

interface LensStyle {
  left: number
  top: number
  backgroundSize: string
  backgroundPosition: string
}

interface ImageMagnifierProps {
  src: string
  alt: string
  zoom?: number
  lensSize?: number
  /** Clockwise rotation in degrees (CSS convention). */
  rotation?: number
  sx?: BoxProps['sx']
  imgSx?: BoxProps['sx']
}

function normalizeRotation(degrees: number): number {
  return ((degrees % 360) + 360) % 360
}

function cssBackgroundUrl(src: string): string {
  // Quote so Firebase download URLs (query tokens, encoded paths) stay valid CSS.
  return `url("${src.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`
}

/** Map a viewport point into the image's unrotated local space (origin at image center). */
function viewportToLocalImageCoords(
  clientX: number,
  clientY: number,
  img: HTMLImageElement,
  rotationDeg: number
): { localX: number; localY: number; displayWidth: number; displayHeight: number } {
  const imgRect = img.getBoundingClientRect()
  // Prefer layout size; fall back to rendered box when offset* is still 0 (lazy load / dialog open).
  const displayWidth = img.offsetWidth || imgRect.width
  const displayHeight = img.offsetHeight || imgRect.height
  const centerX = imgRect.left + imgRect.width / 2
  const centerY = imgRect.top + imgRect.height / 2
  const dx = clientX - centerX
  const dy = clientY - centerY

  // Inverse of CSS rotate(θ): positive θ is clockwise
  const rad = (-rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  return {
    localX: dx * cos - dy * sin,
    localY: dx * sin + dy * cos,
    displayWidth,
    displayHeight,
  }
}

export const ImageMagnifier: React.FC<ImageMagnifierProps> = ({
  src,
  alt,
  zoom = DEFAULT_ZOOM,
  lensSize = DEFAULT_LENS_SIZE,
  rotation = 0,
  sx,
  imgSx,
}) => {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null)
  const [lensVisible, setLensVisible] = useState(false)
  const [lensStyle, setLensStyle] = useState<LensStyle | null>(null)
  const normalizedRotation = normalizeRotation(rotation)
  // Above MUI modal (1300) so the lens shows over Manage Photos / media dialogs.
  const lensZIndex = theme.zIndex.modal + 10

  const updateLensFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      lastPointerRef.current = { x: clientX, y: clientY }
      const img = imageRef.current
      if (!img) return

      const { localX, localY, displayWidth, displayHeight } = viewportToLocalImageCoords(
        clientX,
        clientY,
        img,
        normalizedRotation
      )

      if (displayWidth <= 0 || displayHeight <= 0) return

      if (Math.abs(localX) > displayWidth / 2 || Math.abs(localY) > displayHeight / 2) {
        setLensVisible(false)
        setLensStyle(null)
        return
      }

      const relX = (localX + displayWidth / 2) / displayWidth
      const relY = (localY + displayHeight / 2) / displayHeight
      const halfLens = lensSize / 2
      const lensOffsetY = halfLens + LENS_CURSOR_GAP
      const focusYInLens = lensSize - LENS_CURSOR_GAP
      const zoomedWidth = displayWidth * zoom
      const zoomedHeight = displayHeight * zoom

      // Place the sampled point so that after lens rotation around center, it lands on the focus.
      const fromCenterY = focusYInLens - halfLens
      const invRad = (-normalizedRotation * Math.PI) / 180
      const cos = Math.cos(invRad)
      const sin = Math.sin(invRad)
      const preRotateFocusX = halfLens - fromCenterY * sin
      const preRotateFocusY = halfLens + fromCenterY * cos

      setLensVisible(true)
      setLensStyle({
        left: clientX - halfLens,
        top: clientY - halfLens - lensOffsetY,
        backgroundSize: `${zoomedWidth}px ${zoomedHeight}px`,
        backgroundPosition: `${-(relX * zoomedWidth - preRotateFocusX)}px ${-(relY * zoomedHeight - preRotateFocusY)}px`,
      })
    },
    [lensSize, zoom, normalizedRotation]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      updateLensFromEvent(e.clientX, e.clientY)
    },
    [updateLensFromEvent]
  )

  const handleMouseLeave = useCallback(() => {
    lastPointerRef.current = null
    setLensVisible(false)
    setLensStyle(null)
  }, [])

  const handleImageLoad = useCallback(() => {
    const last = lastPointerRef.current
    if (last) {
      updateLensFromEvent(last.x, last.y)
    }
  }, [updateLensFromEvent])

  useEffect(() => {
    const container = containerRef.current
    const img = imageRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      const last = lastPointerRef.current
      if (last) {
        updateLensFromEvent(last.x, last.y)
      }
    })
    observer.observe(container)
    if (img) observer.observe(img)
    return () => observer.disconnect()
  }, [updateLensFromEvent, src])

  useEffect(() => {
    const repositionLens = () => {
      const last = lastPointerRef.current
      if (last) {
        updateLensFromEvent(last.x, last.y)
      }
    }

    window.addEventListener('scroll', repositionLens, true)
    window.addEventListener('resize', repositionLens)
    return () => {
      window.removeEventListener('scroll', repositionLens, true)
      window.removeEventListener('resize', repositionLens)
    }
  }, [updateLensFromEvent])

  useEffect(() => {
    const last = lastPointerRef.current
    if (last) {
      updateLensFromEvent(last.x, last.y)
    }
  }, [normalizedRotation, updateLensFromEvent])

  const lens =
    lensVisible && lensStyle ? (
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          left: lensStyle.left,
          top: lensStyle.top,
          width: lensSize,
          height: lensSize,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: 'common.white',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 4px 14px rgba(0,0,0,0.4)',
          backgroundImage: cssBackgroundUrl(src),
          backgroundRepeat: 'no-repeat',
          backgroundSize: lensStyle.backgroundSize,
          backgroundPosition: lensStyle.backgroundPosition,
          transform: normalizedRotation ? `rotate(${normalizedRotation}deg)` : undefined,
          pointerEvents: 'none',
          zIndex: lensZIndex,
        }}
      />
    ) : null

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-in',
        ...sx,
      }}
    >
      <Box
        component="img"
        ref={imageRef}
        src={src}
        alt={alt}
        loading="eager"
        onLoad={handleImageLoad}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
          transition: 'transform 0.2s ease',
          ...imgSx,
          // Keep after imgSx so callers can't drop the magnifier's rotation mapping.
          transform: normalizedRotation ? `rotate(${normalizedRotation}deg)` : undefined,
        }}
      />
      {typeof document !== 'undefined' && lens ? createPortal(lens, document.body) : null}
    </Box>
  )
}
